import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  UtensilsCrossed, 
  BarChart3, 
  Users, 
  ClipboardList, 
  Smartphone,
  Check,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
  ChevronRight,
  Clock,
  Shield,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// App configuration from runtime config (set by docker-entrypoint.sh)
const getConfig = (key, defaultValue) => {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__[key]) {
    return window.__RUNTIME_CONFIG__[key];
  }
  return defaultValue;
};

const APP_TITLE = getConfig('REACT_APP_TITLE', 'Het Horeca Dashboard');
const CTA_URL = getConfig('REACT_APP_CTA_URL', '/');

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: {},
  viewport: { once: true },
  transition: { staggerChildren: 0.15 }
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Features", href: "#features" },
    { name: "Prijzen", href: "#pricing" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-stone-100" 
          : "bg-transparent"
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="/" 
            data-testid="logo-link"
            className="flex items-center gap-2 text-stone-900 hover:text-orange-600 transition-colors"
          >
            <UtensilsCrossed className="w-8 h-8 text-orange-600" strokeWidth={1.5} />
            <span className="text-xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
              {APP_TITLE}
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a 
                key={link.name}
                href={link.href}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className="nav-link"
              >
                {link.name}
              </a>
            ))}
            <Button 
              data-testid="nav-cta-button"
              className="btn-primary"
              onClick={() => window.open(CTA_URL, '_blank', 'noopener,noreferrer')}
            >
              Start Nu
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2 text-stone-600 hover:text-orange-600 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-stone-100 py-4"
          >
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                className="block py-3 px-4 text-stone-600 hover:text-orange-600 hover:bg-stone-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <div className="px-4 pt-4">
              <Button 
                data-testid="mobile-cta-button"
                className="btn-primary w-full"
                onClick={() => window.open(CTA_URL, '_blank', 'noopener,noreferrer')}
              >
                Start Nu
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-stone-50 to-stone-100" />
      
      {/* Decorative elements */}
      <div className="absolute top-40 right-10 w-72 h-72 bg-orange-200/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-orange-100/40 rounded-full blur-3xl" />
      
      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div 
            {...fadeInUp}
            className="text-left"
          >
            <span className="caption-text mb-4 block">Voor Horeca Ondernemers</span>
            <h1 className="heading-1 mb-6">
              Het Alles-in-één Dashboard voor{" "}
              <span className="text-orange-600">Horeca</span> Ondernemers
            </h1>
            <p className="body-text mb-8 max-w-lg">
              Beheer uw restaurant, cafe of hotel met één krachtig dashboard.
              Real-time inzicht in je omzet, kosten, Gastbeleving, Marketing, acties en je concurrenten. simpel in 1 overzichtelijk dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                data-testid="hero-cta-button"
                className="btn-primary flex items-center gap-2"
                onClick={() => {
                  toast.success(`Welkom bij ${APP_TITLE}!`);
                  window.open(CTA_URL, '_blank', 'noopener,noreferrer');
                }}
              >
                Start Nu <ChevronRight className="w-5 h-5" />
              </Button>
              <Button 
                data-testid="hero-secondary-button"
                variant="outline"
                className="btn-secondary"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              >
                Ontdek Features
              </Button>
            </div>
            
            {/* Trust indicators */}
            <div className="mt-12 flex items-center gap-6 text-stone-500">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-600" strokeWidth={1.5} />
                <span className="text-sm">Veilig & Betrouwbaar</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/3437689/pexels-photo-3437689.jpeg"
                alt="Restaurant interieur"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent" />
            </div>
            
            {/* Floating stats card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-xl p-5 shadow-xl border border-stone-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-orange-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">+32%</p>
                  <p className="text-sm text-stone-500">Meer Efficiency</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: BarChart3,
      title: "Real-time Inzichten",
      description: "Volg uw omzet en prestaties op de voet, waar u ook bent. Dashboards die direct updates geven over uw bedrijf."
    },
    {
      icon: Users,
      title: "Personeelsplanning",
      description: "Eenvoudig roosters maken en uren registreren. Automatische herinneringen en overzichtelijke planning."
    },
    {
      icon: ClipboardList,
      title: "Voorraadbeheer",
      description: "Nooit meer misgrijpen met slimme voorraadwaarschuwingen. Automatische bestellijsten en kostenbeheer."
    },
    {
      icon: Smartphone,
      title: "Mobiel Toegankelijk",
      description: "Beheer uw zaak vanaf elke locatie. Volledig responsive en geoptimaliseerd voor tablets en smartphones."
    },
    {
      icon: Clock,
      title: "Tijdregistratie",
      description: "Automatische urenregistratie voor uw personeel. Overzichtelijke rapportages voor de salarisadministratie."
    },
    {
      icon: TrendingUp,
      title: "Omzetanalyse",
      description: "Gedetailleerde analyses van uw omzet per dag, week en maand. Ontdek trends en optimaliseer uw menu."
    }
  ];

  return (
    <section 
      id="features"
      data-testid="features-section"
      className="section-spacing bg-white"
    >
      <div className="section-container">
        <motion.div 
          {...fadeInUp}
          className="text-center mb-16"
        >
          <span className="caption-text mb-4 block">Functies</span>
          <h2 className="heading-2 mb-4">
            Alles wat je nodig hebt in 1 overzichtelijk dashboard.
          </h2>
          <p className="body-text max-w-2xl mx-auto">
            Van Hardlopende gerechten, best verkochte arrangementen of het aansturen van je team met de gemaakte doelen.
            Het Horeca dashboard heeft alle tools die je nodig hebt om je met meer inzicht je bedrijf efficiënter te runnen.
          </p>
        </motion.div>

        <motion.div 
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={fadeInUp}
              data-testid={`feature-card-${index}`}
            >
              <Card className="feature-card h-full group">
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-orange-600 transition-colors duration-300">
                  <feature.icon 
                    className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" 
                    strokeWidth={1.5} 
                  />
                </div>
                <h3 className="heading-3 text-xl mb-3">{feature.title}</h3>
                <p className="body-text text-base">{feature.description}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const pricingFeatures = [
    "Onbeperkt aantal gebruikers",
    "Alle functionaliteiten inbegrepen",
    "Dagelijks opzegbaar",
    "Gratis updates",
    "Veilige data-opslag"
  ];

  return (
    <section 
      id="pricing"
      data-testid="pricing-section"
      className="section-spacing bg-gradient-to-b from-stone-50 to-white"
    >
      <div className="section-container">
        <motion.div 
          {...fadeInUp}
          className="text-center mb-16"
        >
          <span className="caption-text mb-4 block">Prijzen</span>
          <h2 className="heading-2 mb-4">
            Eén Simpel Abonnement
          </h2>
          <p className="body-text max-w-2xl mx-auto">
            Geen verborgen kosten, geen limieten. Betaal één prijs en krijg toegang tot alle functies.
          </p>
        </motion.div>

        <motion.div 
          {...fadeInUp}
          className="max-w-lg mx-auto"
        >
          <Card 
            data-testid="pricing-card"
            className="pricing-card text-center"
          >
            {/* Decorative badge */}
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
              <span className="bg-orange-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                Populair
              </span>
            </div>
            
            <div className="pt-4">
              <h3 className="heading-3 mb-2">All-in Pakket</h3>
              <p className="text-stone-500 mb-6">Alles wat u nodig heeft</p>
              
              <div className="mb-8">
                <span className="text-6xl font-bold text-stone-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  €49
                </span>
                <span className="text-xl text-stone-500">/maand</span>
              </div>

              <ul className="space-y-4 mb-8 text-left">
                {pricingFeatures.map((feature, index) => (
                  <li 
                    key={index}
                    data-testid={`pricing-feature-${index}`}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-orange-600" strokeWidth={2} />
                    </div>
                    <span className="text-stone-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                data-testid="pricing-cta-button"
                className="btn-primary w-full text-lg"
                onClick={() => {
                  toast.success("Bedankt voor uw interesse! Neem contact met ons op.");
                  window.location.href = CTA_URL;
                }}
              >
                Start Direct
              </Button>
              
              <p className="mt-4 text-sm text-stone-500">
                Geen creditcard nodig • 14 dagen gratis proberen
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "info@hospitality-app.com",
      href: "mailto:info@hospitality-app.com"
    },
    {
      icon: MapPin,
      label: "Adres",
      value: "Zaailand 106, 8911 BN Leeuwarden",
      href: "https://maps.google.com"
    }
  ];

  return (
    <section 
      id="contact"
      data-testid="contact-section"
      className="section-spacing bg-white"
    >
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div {...fadeInUp}>
            <span className="caption-text mb-4 block">Contact</span>
            <h2 className="heading-2 mb-4">
              Neem Contact Op
            </h2>
            <p className="body-text mb-8 max-w-lg">
              Heeft u vragen over {APP_TITLE}? Ons team staat voor u klaar om 
              al uw vragen te beantwoorden.
            </p>

            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  data-testid={`contact-${item.label.toLowerCase()}`}
                  variants={fadeInUp}
                  className="flex items-center gap-4 p-4 rounded-xl bg-stone-50 hover:bg-orange-50 transition-colors group"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-600 transition-colors">
                    <item.icon 
                      className="w-6 h-6 text-orange-600 group-hover:text-white transition-colors" 
                      strokeWidth={1.5} 
                    />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">{item.label}</p>
                    <p className="text-stone-900 font-medium">{item.value}</p>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="/images/horeca-team.jpg"
                alt="Horeca team"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const footerLinks = {
    product: [
      { name: "Features", href: "#features" },
      { name: "Prijzen", href: "#pricing" },
      { name: "Contact", href: "#contact" }
    ],
    bedrijf: [
      { name: "Over Ons", href: "#" }
    ],
    legal: [
      { name: "Privacy", href: "#" },
      { name: "Voorwaarden", href: "#" }
    ]
  };

  return (
    <footer 
      data-testid="footer"
      className="bg-stone-900 text-white py-16"
    >
      <div className="section-container">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <a href="/" className="flex items-center gap-2 mb-4">
              <UtensilsCrossed className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
              <span className="text-xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
                {APP_TITLE}
              </span>
            </a>
            <p className="text-stone-400 text-sm leading-relaxed">
              De alles-in-één oplossing voor horeca ondernemers in Nederland.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                    className="text-stone-400 hover:text-orange-500 transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-white">Bedrijf</h4>
            <ul className="space-y-3">
              {footerLinks.bedrijf.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-stone-400 hover:text-orange-500 transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-white">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-stone-400 hover:text-orange-500 transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-stone-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-stone-500 text-sm">
            © {new Date().getFullYear()} {APP_TITLE}. Alle rechten voorbehouden.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-stone-500 text-sm">Gemaakt met ❤️ in Nederland</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
      <ContactSection />
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
